import React from 'react';
import { Dropdown, Avatar, Progress, Flex } from 'antd';
import { MoreOutlined, CrownFilled, UserOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { UserDisplay } from './types/UserDisplay';
import { formatPhoneNumber } from '@leedpulse-monorepo/utils';
import { ECreditType } from '@leedpulse-monorepo/enums';
import { getUserPlanBadgeClass, getUserStatusBadgeClass, getPlanCreditsRemaining, getPlanCreditsOverageState } from '../../helpers';
import { getActionMenuItems } from './helpers/usersPageHelpers';
import { ModalType } from './controllers/useUsersPageController';
import { DegradedSubscription } from '../../contexts/auth/AuthenticationContext';

interface GetUsersColumnsParams {
  openModal: (type: ModalType, user?: UserDisplay) => void;
  degradedSubscriptions: DegradedSubscription[];
  isCompanyExternallyBilled: boolean;
}

export const getUsersColumns = ({
  openModal,
  degradedSubscriptions,
  isCompanyExternallyBilled,
}: GetUsersColumnsParams): ColumnsType<UserDisplay> => {
  return [
    {
      title: 'Utilisateurs',
      dataIndex: 'name',
      key: 'user',
      render: (_, record) => (
        <div className="users-page__user-cell">
          {record.avatar ? (
            <img src={record.avatar} alt={record.name} className="users-page__user-avatar" />
          ) : (
            <Avatar icon={<UserOutlined />} className="users-page__user-avatar" />
          )}
          <div className="users-page__user-info">
            <span className="users-page__user-name">
              {record.name}
              {record.isVip && <CrownFilled className="users-page__vip-crown" />}
            </span>
            <span className="users-page__user-email">{record.email}</span>
          </div>
        </div>
      ),
    },
    {
      title: 'Plan',
      dataIndex: 'plan',
      key: 'plan',
      render: (_, record) => (
        <Flex align="center" justify="flex-start" gap="8px">
          {record.sender.length > 0 && (
            <Flex className="users-page__phone" style={{ whiteSpace: 'nowrap' }}>{record.sender.map(sender => formatPhoneNumber(sender)).join(', ')}</Flex> // Not supposed to have several sender 
          )}
          {record.plan ? (
            <Flex className={`users-page__plan-badge ${getUserPlanBadgeClass(record.plan)}`} justify="center">
              {record.plan}
            </Flex>
          ) : (
            <span className="users-page__plan-badge users-page__plan-badge--non-assigne">
              Non assigné
            </span>
          )}
        </Flex>
      ),
    },
    {
      title: 'Forfait restant',
      dataIndex: 'credits_info',
      key: 'credits',
      render: (_, record) => {
        const info = record.credits_info;

        // No subscription / number → -
        if (!info) {
          return <span className="users-page__credits-na">-</span>;
        }

        if (info.isUnlimitedLeedbucks) {
          return <span className="users-page__credits-unlimited">∞</span>;
        }

        const total = info.total?.[ECreditType.leedbucks] || 0;
        const remaining = info.remaining?.[ECreditType.leedbucks] || 0;
        const planCredits = getPlanCreditsRemaining(total, remaining, false);

        return (
          <Flex align="center" gap={6} style={{ minWidth: 100 }}>
            <Progress
              percent={planCredits.remainingPercent}
              showInfo={false}
              strokeColor={planCredits.barColor}
              className="users-page__credits-bar"
              style={{ flex: 1, margin: 0 }}
            />
            <span className={`users-page__credits-percentage${planCredits.amber ? ' users-page__credits-percentage--amber' : ''}`}>{planCredits.remainingPercent}%</span>
          </Flex>
        );
      },
    },
    {
      title: 'Dernière activité',
      dataIndex: 'lastActivity',
      key: 'lastActivity',
      render: (activity: string | null) => (
        <span className={`users-page__activity ${!activity ? 'users-page__activity--none' : ''}`}>
          {activity || '-'}
        </span>
      ),
    },
    {
      title: 'Statut',
      dataIndex: 'status',
      key: 'status',
      render: (status: UserDisplay['status'], record) => {
        const info = record.credits_info;
        const total = info?.total?.[ECreditType.leedbucks] || 0;
        const remaining = info?.remaining?.[ECreditType.leedbucks] || 0;
        const planCredits = info && !info.isUnlimitedLeedbucks ? getPlanCreditsRemaining(total, remaining, false) : null;
        const overageState = planCredits && status === 'Actif' ? getPlanCreditsOverageState(planCredits.exhausted, info?.payg_enabled) : null;

        if (overageState === 'disabled') {
          return <span className="users-page__status-badge users-page__status-badge--overage-disabled">Hors forfait désactivé</span>;
        }
        if (overageState === 'overage') {
          return <span className="users-page__status-badge users-page__status-badge--overage">Hors forfait</span>;
        }
        return (
          <span className={`users-page__status-badge ${getUserStatusBadgeClass(status)}`}>
            {status}
          </span>
        );
      },
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => (
        <div onClick={(e) => e.stopPropagation()}>
          <Dropdown
            menu={{ items: getActionMenuItems(record, openModal, degradedSubscriptions.some((d) => d.id === record.idSubscription), isCompanyExternallyBilled) }}
            trigger={['click']}
            placement="bottomRight"
          >
            <button className="users-page__action-button">
              <MoreOutlined style={{ fontSize: 18 }} />
            </button>
          </Dropdown>
        </div>
      ),
    },
  ];
};
